import taskDataStore from './taskDataStore.js';
import TasksPublicServices from './TasksPublicServices.js';
import { API_ERRORS, createApiError, wrapResponse } from '../../utils/apiError.js';
import {
  normalizeUser,
  canReadTask,
  canWriteTask,
  isWriteAction,
  isAdmin,
} from '../../utils/taskAuth.js';
import {
  validateTaskCreate,
  validateStatus,
  validatePriority,
  validateDueDate,
  validateVisibility,
  validateTags,
  validateTitle,
  validateDescription,
  validateChecklist,
} from '../../utils/taskValidation.js';
import {
  normalizeChecklist,
  cleanChecklist,
  createChecklistItem,
  markChecklistCompleted,
  getCompletionPercentage,
  getChecklistStats,
  deepCopyChecklist,
  findChecklistItem,
  buildAISummary,
  applyStatusSideEffects,
  paginateItems,
  sortTasks,
  filterTasksByQuery,
  computeTaskStats,
  collectTags,
  isTaskOverdue,
  isTaskIncomplete,
  toPublicTask,
} from '../../utils/taskHelpers.js';

const ROOT_CHECKLIST_LIMIT = 50;
const CHILD_CHECKLIST_LIMIT = 30;

const now = () => new Date().toISOString();

const normalizeTask = (task) => {
  if (!task) return null;
  return {
    ...task,
    visibility: task.visibility || 'private',
    completed: !!task.completed,
    tags: task.tags || [],
    checklist: normalizeChecklist(task.checklist || []),
  };
};

const buildChanges = (before, after, fields) => fields
  .filter((f) => before[f] !== after[f])
  .map((f) => ({ field: f, old_value: before[f], new_value: after[f] }));

const fail = (error, action) => wrapResponse({
  success: false,
  action,
  error: { code: error.code, message: error.message, details: error.details },
});

const ok = (action, data, summary, changes) => wrapResponse({
  success: true,
  action,
  data,
  summary,
  changes,
});

const fetchAllTasks = async () => {
  const items = await taskDataStore.getAll();
  return items.map(normalizeTask);
};

const fetchTask = async (taskId) => {
  const result = await taskDataStore.getById(taskId);
  if (!result) return null;
  return normalizeTask(result);
};

const persistTask = async (taskId, data) => {
  const result = await taskDataStore.update(taskId, {
    ...data,
    updated_at: now(),
  });
  return normalizeTask(result);
};

const filterAccessibleTasks = (tasks, user) => {
  if (isAdmin(user)) return tasks;
  return tasks.filter((t) => canReadTask(t, user));
};

const applyListFilters = (tasks, payload = {}) => {
  let filtered = [...tasks];
  if (payload.status) filtered = filtered.filter((t) => t.status === payload.status);
  if (payload.priority) filtered = filtered.filter((t) => t.priority === payload.priority);
  if (payload.tag) filtered = filtered.filter((t) => (t.tags || []).includes(payload.tag));
  if (payload.overdue) filtered = filtered.filter((t) => isTaskOverdue(t.due_date, t.status));
  if (payload.q) filtered = filterTasksByQuery(filtered, payload.q);
  if (payload.visibility) filtered = filtered.filter((t) => t.visibility === payload.visibility);
  return filtered;
};

const requireTask = async (taskId, user, action, write = false) => {
  const task = await fetchTask(taskId);
  if (!task) return { error: fail(createApiError(API_ERRORS.NOT_FOUND), action) };
  if (write && !canWriteTask(task, user)) {
    return { error: fail(createApiError(API_ERRORS.FORBIDDEN), action) };
  }
  if (!write && !canReadTask(task, user)) {
    return { error: fail(createApiError(API_ERRORS.FORBIDDEN), action) };
  }
  return { task };
};

const saveChecklist = async (task, checklist, action) => {
  const cleaned = cleanChecklist(normalizeChecklist(checklist));
  const updated = await persistTask(task.id, { checklist: cleaned });
  return ok(action, {
    task: updated,
    checklist: updated.checklist,
    completion_percentage: getCompletionPercentage(updated),
  }, buildAISummary(action, updated));
};

const TasksAIServices = {
  // ─── READ ───────────────────────────────────────────────
  listTasks: async (payload = {}, user) => {
    const action = 'tasks.list';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const all = await fetchAllTasks();
    const accessible = filterAccessibleTasks(all, user);
    const filtered = applyListFilters(accessible, payload);
    const sorted = sortTasks(filtered, payload.sort || 'created_at', payload.order || 'desc');
    const { items, pagination } = paginateItems(sorted, payload);
    return ok(action, { items, pagination }, `${pagination.totalItems} görev listelendi.`);
  },

  getTask: async (taskId, user) => {
    const action = 'tasks.get';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const { task, error } = await requireTask(taskId, user, action);
    if (error) return error;
    return ok(action, { task }, `'${task.title}' detayı getirildi.`);
  },

  searchTasks: async (payload = {}, user) => {
    const action = 'tasks.search';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const all = await fetchAllTasks();
    const accessible = filterAccessibleTasks(all, user);
    const filtered = applyListFilters(accessible, payload);
    const sorted = sortTasks(filtered, payload.sort || 'created_at', payload.order || 'desc');
    const { items, pagination } = paginateItems(sorted, payload);
    return ok(action, { items, pagination }, `${pagination.totalItems} sonuç bulundu.`);
  },

  getStats: async (user) => {
    const action = 'tasks.stats';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const all = await fetchAllTasks();
    const accessible = filterAccessibleTasks(all, user);
    const stats = computeTaskStats(accessible);
    return ok(action, { stats }, `${stats.total} görev.`);
  },

  getTags: async (user) => {
    const action = 'tasks.tags';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const all = await fetchAllTasks();
    const accessible = filterAccessibleTasks(all, user);
    return ok(action, { tags: collectTags(accessible) });
  },

  getOverdueTasks: async (user) => {
    const action = 'tasks.search';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    return TasksAIServices.searchTasks({ overdue: true }, user);
  },

  getNextIncompleteTask: async (payload = {}, user) => {
    const action = 'tasks.next';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);

    const all = await fetchAllTasks();
    const accessible = filterAccessibleTasks(all, user);
    const incomplete = accessible.filter(isTaskIncomplete);
    const sorted = sortTasks(incomplete, payload.sort || 'created_at', payload.order || 'asc');

    if (!sorted.length) {
      return fail(createApiError(API_ERRORS.NOT_FOUND, 'Tamamlanmamış görev bulunamadı'), action);
    }

    let task = sorted[0];
    const markInProgress = payload.mark_in_progress === true
      || payload.mark_in_progress === 'true'
      || payload.mark_in_progress === '1'
      || payload.mark_in_progress === 1;

    if (markInProgress && task.status !== 'in-progress') {
      const statusResult = await TasksAIServices.setStatus(task.id, 'in-progress', user);
      if (statusResult.success) {
        task = statusResult.data.task;
      }
    }

    return ok(
      action,
      { task, pending_count: sorted.length },
      `'${task.title}' sıradaki görev olarak seçildi.`,
    );
  },

  // ─── CREATE / UPDATE / DELETE ───────────────────────────
  createTask: async (payload = {}, user) => {
    const action = 'tasks.create';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);

    const validationError = validateTaskCreate(payload);
    if (validationError) return fail(validationError, action);

    const taskData = {
      title: String(payload.title).trim(),
      description: payload.description || '',
      status: payload.status || 'pending',
      priority: payload.priority || 'medium',
      due_date: payload.due_date || '',
      notes: payload.notes || '',
      completed: payload.status === 'completed',
      tags: payload.tags || [],
      checklist: cleanChecklist(normalizeChecklist(payload.checklist || [])),
      assigned_to: payload.assigned_to || null,
      visibility: payload.visibility || 'private',
      created_by: user.id,
      created_at: now(),
      updated_at: now(),
      keywords: true,
    };

    if (taskData.status === 'completed') {
      taskData.checklist = markChecklistCompleted(taskData.checklist, true);
    }

    let result;
    try {
      result = await taskDataStore.add(taskData);
    } catch (e) {
      return fail(createApiError(API_ERRORS.INTERNAL_ERROR, e.message), action);
    }
    const task = normalizeTask(result);
    return ok(action, { task }, buildAISummary(action, task));
  },

  updateTask: async (taskId, payload = {}, user) => {
    const action = 'tasks.update';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;

    const updates = { ...payload };
    delete updates.id;
    if (updates.checklist) updates.checklist = cleanChecklist(normalizeChecklist(updates.checklist));
    if (updates.status) Object.assign(updates, applyStatusSideEffects(updates.status, { ...task, ...updates }));

    const updated = await persistTask(taskId, updates);
    const changes = buildChanges(task, updated, Object.keys(updates));
    return ok(action, { task: updated }, buildAISummary(action, updated), changes);
  },

  deleteTask: async (taskId, payload = {}, user) => {
    const action = 'tasks.delete';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    if (!payload?.confirm) return fail(createApiError(API_ERRORS.CONFIRMATION_REQUIRED), action);

    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;

    try {
      await taskDataStore.delete(taskId);
    } catch (e) {
      return fail(createApiError(API_ERRORS.INTERNAL_ERROR, e.message), action);
    }
    return ok(action, { id: taskId }, buildAISummary(action, task));
  },

  // ─── STATUS & FIELDS ────────────────────────────────────
  completeTask: async (taskId, user) => {
    const action = 'tasks.complete';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const before = { ...task };
    const updates = applyStatusSideEffects('completed', task);
    const updated = await persistTask(taskId, updates);
    const changes = buildChanges(before, updated, ['status', 'completed']);
    return ok(action, { task: updated }, buildAISummary(action, updated), changes);
  },

  reopenTask: async (taskId, user) => {
    const action = 'tasks.reopen';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const before = { ...task };
    const updated = await persistTask(taskId, { status: 'pending', completed: false });
    const changes = buildChanges(before, updated, ['status', 'completed']);
    return ok(action, { task: updated }, buildAISummary(action, updated), changes);
  },

  setStatus: async (taskId, status, user) => {
    const action = 'tasks.set_status';
    const err = validateStatus(status);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const before = { ...task };
    const updates = applyStatusSideEffects(status, task);
    const updated = await persistTask(taskId, updates);
    const changes = buildChanges(before, updated, ['status', 'completed']);
    return ok(action, { task: updated }, buildAISummary(action, updated), changes);
  },

  setPriority: async (taskId, priority, user) => {
    const action = 'tasks.set_priority';
    const err = validatePriority(priority);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { priority });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  setDueDate: async (taskId, dueDate, user) => {
    const action = 'tasks.set_due_date';
    const err = validateDueDate(dueDate);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { due_date: dueDate || '' });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  setTitle: async (taskId, title, user) => {
    const action = 'tasks.set_title';
    const err = validateTitle(title);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { title: String(title).trim() });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  setDescription: async (taskId, description, user) => {
    const action = 'tasks.set_description';
    const err = validateDescription(description);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { description: description || '' });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  updateNotes: async (taskId, notes, user) => {
    const action = 'tasks.update_notes';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { notes: notes || '' });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  appendNotes: async (taskId, notes, user) => {
    const action = 'tasks.append_notes';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const existing = task.notes || '';
    const appended = existing ? `${existing}\n\n${notes}` : notes;
    const updated = await persistTask(taskId, { notes: appended });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  setVisibility: async (taskId, visibility, user) => {
    const action = 'tasks.set_visibility';
    const err = validateVisibility(visibility);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { visibility });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  assignTask: async (taskId, assignedTo, user) => {
    const action = 'tasks.assign';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { assigned_to: assignedTo });
    return ok(action, { task: updated }, `'${task.title}' atandı.`);
  },

  unassignTask: async (taskId, user) => {
    const action = 'tasks.unassign';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { assigned_to: null });
    return ok(action, { task: updated }, `'${task.title}' ataması kaldırıldı.`);
  },

  // ─── TAGS ───────────────────────────────────────────────
  addTags: async (taskId, tags, user) => {
    const action = 'tasks.add_tags';
    const err = validateTags(tags);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const merged = [...new Set([...(task.tags || []), ...tags])];
    const updated = await persistTask(taskId, { tags: merged });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  removeTags: async (taskId, tags, user) => {
    const action = 'tasks.remove_tags';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const filtered = (task.tags || []).filter((t) => !tags.includes(t));
    const updated = await persistTask(taskId, { tags: filtered });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  setTags: async (taskId, tags, user) => {
    const action = 'tasks.set_tags';
    const err = validateTags(tags);
    if (err) return fail(err, action);
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const updated = await persistTask(taskId, { tags: tags || [] });
    return ok(action, { task: updated }, buildAISummary(action, updated));
  },

  // ─── CHECKLIST (root) ───────────────────────────────────
  addChecklistItem: async (taskId, text, user) => {
    const action = 'tasks.checklist.add';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    if (checklist.length >= ROOT_CHECKLIST_LIMIT) {
      return fail(createApiError(API_ERRORS.CHECKLIST_LIMIT_EXCEEDED), action);
    }
    checklist.push(createChecklistItem(text));
    return saveChecklist(task, checklist, action);
  },

  updateChecklistItem: async (taskId, itemId, patch, user) => {
    const action = 'tasks.checklist.update';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const found = findChecklistItem(checklist, itemId);
    if (!found || found.isChild) {
      return fail(createApiError(API_ERRORS.CHECKLIST_ITEM_NOT_FOUND), action);
    }
    if (patch.text !== undefined) found.item.text = patch.text;
    if (patch.completed !== undefined) found.item.completed = patch.completed;
    return saveChecklist(task, checklist, action);
  },

  toggleChecklistItem: async (taskId, itemId, completed, user) => {
    const action = 'tasks.checklist.toggle';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const found = findChecklistItem(checklist, itemId);
    if (!found) return fail(createApiError(API_ERRORS.CHECKLIST_ITEM_NOT_FOUND), action);
    found.item.completed = completed !== undefined ? completed : !found.item.completed;
    return saveChecklist(task, checklist, action);
  },

  removeChecklistItem: async (taskId, itemId, user) => {
    const action = 'tasks.checklist.remove';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist).filter((i) => String(i.id) !== String(itemId));
    return saveChecklist(task, checklist, action);
  },

  completeAllChecklistItems: async (taskId, user) => {
    const action = 'tasks.checklist.complete_all';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = markChecklistCompleted(normalizeChecklist(task.checklist), true);
    return saveChecklist(task, checklist, action);
  },

  // ─── CHECKLIST (children) ───────────────────────────────
  addChecklistChildItem: async (taskId, parentItemId, text, user) => {
    const action = 'tasks.checklist.child.add';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const parent = checklist.find((i) => String(i.id) === String(parentItemId));
    if (!parent) return fail(createApiError(API_ERRORS.CHECKLIST_PARENT_NOT_FOUND), action);
    parent.children = parent.children || [];
    if (parent.children.length >= CHILD_CHECKLIST_LIMIT) {
      return fail(createApiError(API_ERRORS.CHECKLIST_LIMIT_EXCEEDED), action);
    }
    const child = createChecklistItem(text);
    parent.children.push(child);
    const result = await saveChecklist(task, checklist, action);
    if (result.success) {
      result.data.parent_item_id = parentItemId;
      result.data.child = child;
      result.summary = `Alt madde '${text}' eklendi.`;
    }
    return result;
  },

  updateChecklistChildItem: async (taskId, parentItemId, childId, patch, user) => {
    const action = 'tasks.checklist.child.update';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const parent = checklist.find((i) => String(i.id) === String(parentItemId));
    if (!parent) return fail(createApiError(API_ERRORS.CHECKLIST_PARENT_NOT_FOUND), action);
    const child = (parent.children || []).find((c) => String(c.id) === String(childId));
    if (!child) return fail(createApiError(API_ERRORS.CHECKLIST_CHILD_NOT_FOUND), action);
    if (patch.text !== undefined) child.text = patch.text;
    if (patch.completed !== undefined) child.completed = patch.completed;
    return saveChecklist(task, checklist, action);
  },

  toggleChecklistChildItem: async (taskId, parentItemId, childId, completed, user) => {
    const action = 'tasks.checklist.child.toggle';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const parent = checklist.find((i) => String(i.id) === String(parentItemId));
    if (!parent) return fail(createApiError(API_ERRORS.CHECKLIST_PARENT_NOT_FOUND), action);
    const child = (parent.children || []).find((c) => String(c.id) === String(childId));
    if (!child) return fail(createApiError(API_ERRORS.CHECKLIST_CHILD_NOT_FOUND), action);
    child.completed = completed !== undefined ? completed : !child.completed;
    return saveChecklist(task, checklist, action);
  },

  removeChecklistChildItem: async (taskId, parentItemId, childId, user) => {
    const action = 'tasks.checklist.child.remove';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;
    const checklist = normalizeChecklist(task.checklist);
    const parent = checklist.find((i) => String(i.id) === String(parentItemId));
    if (!parent) return fail(createApiError(API_ERRORS.CHECKLIST_PARENT_NOT_FOUND), action);
    parent.children = (parent.children || []).filter((c) => String(c.id) !== String(childId));
    return saveChecklist(task, checklist, action);
  },

  // ─── BULK & DUPLICATE ───────────────────────────────────
  duplicateTask: async (taskId, user) => {
    const action = 'tasks.duplicate';
    const { task, error } = await requireTask(taskId, user, action, true);
    if (error) return error;

    const copy = {
      title: `${task.title} (kopya)`,
      description: task.description || '',
      status: 'pending',
      priority: task.priority,
      due_date: task.due_date || '',
      notes: task.notes || '',
      completed: false,
      tags: [...(task.tags || [])],
      checklist: deepCopyChecklist(task.checklist),
      assigned_to: task.assigned_to,
      visibility: task.visibility || 'private',
      created_by: user.id,
      created_at: now(),
      updated_at: now(),
      keywords: true,
    };

    let result;
    try {
      result = await taskDataStore.add(copy);
    } catch (e) {
      return fail(createApiError(API_ERRORS.INTERNAL_ERROR, e.message), action);
    }
    const newTask = normalizeTask(result);
    return ok(action, { task: newTask }, buildAISummary(action, newTask));
  },

  bulkUpdate: async (taskIds = [], updates = {}, user) => {
    const action = 'tasks.bulk_update';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const results = [];
    for (const id of taskIds) {
      const res = await TasksAIServices.updateTask(id, updates, user);
      results.push(res);
    }
    const successCount = results.filter((r) => r.success).length;
    return ok(action, { results, updated: successCount }, buildAISummary(action, null, `${successCount} görev güncellendi.`));
  },

  bulkComplete: async (taskIds = [], user) => {
    const action = 'tasks.bulk_complete';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    const results = [];
    for (const id of taskIds) {
      results.push(await TasksAIServices.completeTask(id, user));
    }
    const successCount = results.filter((r) => r.success).length;
    return ok(action, { results, completed: successCount }, buildAISummary(action, null, `${successCount} görev tamamlandı.`));
  },

  bulkDelete: async (taskIds = [], payload = {}, user) => {
    const action = 'tasks.bulk_delete';
    if (!user) return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    if (!payload?.confirm) return fail(createApiError(API_ERRORS.CONFIRMATION_REQUIRED), action);
    const results = [];
    for (const id of taskIds) {
      results.push(await TasksAIServices.deleteTask(id, { confirm: true }, user));
    }
    const successCount = results.filter((r) => r.success).length;
    return ok(action, { results, deleted: successCount }, buildAISummary(action, null, `${successCount} görev silindi.`));
  },

  // ─── AI EXECUTE (tek kapı) ──────────────────────────────
  executeAction: async ({ action, task_id, task_ids, payload = {} }, rawUser) => {
    const user = normalizeUser(rawUser);

    if (!action) {
      return fail(createApiError(API_ERRORS.UNKNOWN_ACTION, 'action zorunlu'), 'unknown');
    }

    if (!action.startsWith('tasks.public.') && !user) {
      return fail(createApiError(API_ERRORS.UNAUTHORIZED), action);
    }

    if (isWriteAction(action) && !action.startsWith('tasks.public.') && action !== 'tasks.create') {
      if (task_id) {
        const task = await fetchTask(task_id);
        if (task && !canWriteTask(task, user)) {
          return fail(createApiError(API_ERRORS.FORBIDDEN), action);
        }
      }
    }

    try {
      switch (action) {
        // Public read
        case 'tasks.public.list':
          return TasksPublicServices.getPublicItems(payload);
        case 'tasks.public.get':
          return TasksPublicServices.getPublicItem({ id: task_id });
        case 'tasks.public.search':
          return TasksPublicServices.searchPublicItems(payload);
        case 'tasks.public.stats':
          return TasksPublicServices.getPublicStats();

        // Auth read
        case 'tasks.list':
          return TasksAIServices.listTasks(payload, user);
        case 'tasks.get':
          return TasksAIServices.getTask(task_id, user);
        case 'tasks.search':
          return TasksAIServices.searchTasks(payload, user);
        case 'tasks.stats':
          return TasksAIServices.getStats(user);
        case 'tasks.next':
          return TasksAIServices.getNextIncompleteTask(payload, user);

        // Write — task
        case 'tasks.create':
          return TasksAIServices.createTask(payload, user);
        case 'tasks.update':
          return TasksAIServices.updateTask(task_id, payload, user);
        case 'tasks.delete':
          return TasksAIServices.deleteTask(task_id, payload, user);
        case 'tasks.duplicate':
          return TasksAIServices.duplicateTask(task_id, user);
        case 'tasks.complete':
          return TasksAIServices.completeTask(task_id, user);
        case 'tasks.reopen':
          return TasksAIServices.reopenTask(task_id, user);

        // Write — fields
        case 'tasks.set_status':
          return TasksAIServices.setStatus(task_id, payload.status, user);
        case 'tasks.set_priority':
          return TasksAIServices.setPriority(task_id, payload.priority, user);
        case 'tasks.set_due_date':
          return TasksAIServices.setDueDate(task_id, payload.due_date, user);
        case 'tasks.set_title':
          return TasksAIServices.setTitle(task_id, payload.title, user);
        case 'tasks.set_description':
          return TasksAIServices.setDescription(task_id, payload.description, user);
        case 'tasks.update_notes':
          return TasksAIServices.updateNotes(task_id, payload.notes, user);
        case 'tasks.append_notes':
          return TasksAIServices.appendNotes(task_id, payload.notes, user);
        case 'tasks.set_visibility':
          return TasksAIServices.setVisibility(task_id, payload.visibility, user);
        case 'tasks.assign':
          return TasksAIServices.assignTask(task_id, payload.assigned_to, user);
        case 'tasks.unassign':
          return TasksAIServices.unassignTask(task_id, user);

        // Write — tags
        case 'tasks.add_tags':
          return TasksAIServices.addTags(task_id, payload.tags, user);
        case 'tasks.remove_tags':
          return TasksAIServices.removeTags(task_id, payload.tags, user);
        case 'tasks.set_tags':
          return TasksAIServices.setTags(task_id, payload.tags, user);

        // Write — checklist root
        case 'tasks.checklist.add':
          return TasksAIServices.addChecklistItem(task_id, payload.checklist_item_text, user);
        case 'tasks.checklist.update':
          return TasksAIServices.updateChecklistItem(task_id, payload.checklist_item_id, {
            text: payload.checklist_item_text,
            completed: payload.checklist_item_completed,
          }, user);
        case 'tasks.checklist.toggle':
          return TasksAIServices.toggleChecklistItem(task_id, payload.checklist_item_id, payload.checklist_item_completed, user);
        case 'tasks.checklist.remove':
          return TasksAIServices.removeChecklistItem(task_id, payload.checklist_item_id, user);
        case 'tasks.checklist.complete_all':
          return TasksAIServices.completeAllChecklistItems(task_id, user);

        // Write — checklist children
        case 'tasks.checklist.child.add':
          return TasksAIServices.addChecklistChildItem(task_id, payload.checklist_parent_item_id, payload.checklist_item_text, user);
        case 'tasks.checklist.child.update':
          return TasksAIServices.updateChecklistChildItem(task_id, payload.checklist_parent_item_id, payload.checklist_child_item_id, {
            text: payload.checklist_item_text,
            completed: payload.checklist_item_completed,
          }, user);
        case 'tasks.checklist.child.toggle':
          return TasksAIServices.toggleChecklistChildItem(task_id, payload.checklist_parent_item_id, payload.checklist_child_item_id, payload.checklist_item_completed, user);
        case 'tasks.checklist.child.remove':
          return TasksAIServices.removeChecklistChildItem(task_id, payload.checklist_parent_item_id, payload.checklist_child_item_id, user);

        // Bulk
        case 'tasks.bulk_update':
          return TasksAIServices.bulkUpdate(task_ids, payload, user);
        case 'tasks.bulk_complete':
          return TasksAIServices.bulkComplete(task_ids, user);
        case 'tasks.bulk_delete':
          return TasksAIServices.bulkDelete(task_ids, payload, user);

        default:
          return fail(createApiError(API_ERRORS.UNKNOWN_ACTION, `Bilinmeyen aksiyon: ${action}`), action);
      }
    } catch (e) {
      console.error('executeAction error:', e);
      return fail(createApiError(API_ERRORS.INTERNAL_ERROR, e.message), action);
    }
  },
};

export default TasksAIServices;
